import React, { useState, useEffect } from 'react';
import { useTranslation }  from 'react-i18next';
import { RootState, useAppSelector, useAppDispatch } from '../../redux';
import { resetRoutine, loadRoutines, createRoutine, deleteRoutine } from '../../redux';
import { Icon } from '../Icon';
import { Confirm } from '../Confirm';
import { Routine } from '../../models';
import { Button } from '../../components';
import { RoutineForm } from './RoutineForm';
import RoutinesApi from '../../services/Routines';
import { Alert } from '../Alert';

interface RoutinesManagerProps {
    authorId?: string;
    projectId?: string;
    changeId?: string;
}

function RoutinesManager(props: RoutinesManagerProps) {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const [active, setActive]                 = useState<string|null>(null);
    const [toDelete, setToDelete]             = useState<string|null>(null);
    const [newRoutineName, setNewRoutineName] = useState();
    const [seeding, setSeeding]               = useState(false);
    const [runs, setRuns]                     = useState<AnyObj[]>([]);
    const [seedFeedback, setSeedFeedback]     = useState<{ tone: 'success' | 'warning' | 'failure', message: string } | null>(null);

    const { authorId, projectId, changeId } = props;

    const { routines } = useAppSelector((state: RootState) => state.routines);

    const reload = () => {
        dispatch(loadRoutines({ author: authorId, project: projectId, change: changeId }));
        if (projectId) {
            RoutinesApi.listRuns({ project: projectId, limit: 10 })
                .then((json) => setRuns(json.runs || []))
                .catch(() => setRuns([]));
        }
    };

    useEffect(() => {
        reload();
    }, [dispatch, authorId, projectId, changeId]);

    const sortedRoutines = [...(routines || [])].sort((a: Routine, b: Routine) =>
        (a.priority ?? 100) - (b.priority ?? 100)
    );

    const addRoutine = () => {
        const callback = (err: Error | null, newRoutine?: AnyObj | AnyObj[] | null) => {
            setNewRoutineName(undefined);
            setActive((newRoutine as Routine)?._id);
        };

        dispatch(createRoutine(new Routine({
            name: newRoutineName,
            author: authorId,
            project: projectId,
            change: changeId,
            enabled: true,
            priority: 100,
            stopAfter: false,
            conditions: []
        }), /*params*/{}, callback));
    };

    const duplicateRoutine = (routine: Routine) => {
        const callback = (err: Error | null, newRoutine?: AnyObj | AnyObj[] | null) => {
            setNewRoutineName(undefined);
            setActive((newRoutine as Routine)?._id);
        };

        const { _id, ...props } = routine;
        dispatch(createRoutine(new Routine({
            ...props,
            name: `${props.name} - Copy`,
            author: authorId,
        }), /*params*/{}, callback));
    };

    const removeRoutine = () => {
        const callback = () => {
            setToDelete(null);
        };

        if (toDelete)
            dispatch(deleteRoutine(toDelete, callback));
    };

    const closeRoutine = () => {
        setActive(null);
        dispatch(resetRoutine(/*onlyItem*/true));
    };

    const seedExamples = async () => {
        if (!projectId)
            return;
        setSeeding(true);
        setSeedFeedback(null);
        try {
            const result = await RoutinesApi.seed(projectId);
            reload();
            const created = result?.createdCount ?? result?.routines?.length ?? 0;
            const skipped = result?.skippedCount ?? 0;
            if (created > 0) {
                setSeedFeedback({
                    tone: 'success',
                    message: t('projects.routine.seed_created', { count: created })
                });
            } else {
                setSeedFeedback({
                    tone: 'warning',
                    message: t('projects.routine.seed_already_present', { count: skipped })
                });
            }
        } catch (err: any) {
            setSeedFeedback({
                tone: 'failure',
                message: err?.message || t('projects.routine.seed_failed')
            });
        } finally {
            setSeeding(false);
        }
    };

    const handleKeyPress = (e: AnyObj, enterCallback: (shift: boolean) => void, escapeCallback?: () => void) => {
        if (e.key === 'Enter' || e.code === 'Enter')
            enterCallback(!e.shiftKey);
        if (escapeCallback && (e.key === 'Escape' || e.code === 'Escape'))
            escapeCallback();
    }

    const routineForm = React.useMemo(() => (routine: Routine) => {
        return (
            <div key={routine._id} className="flex flex-col">
                <div onClick={() => setActive(active === routine._id ? null : routine._id)} className={`cursor-pointer flex justify-between items-center routine border rounded ${active === routine._id ? 'rounded-b-none bg-gray-50 dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-900'}`}>
                    <div className="text-gray-900 dark:text-gray-100 p-2 flex flex-row items-center space-x-2">
                        <Icon type="routine" />
                        <h3>{routine.name}</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">#{routine.priority ?? 100}</span>
                        {routine.enabled === false && (
                            <span className="text-xs text-orange-600">{t('projects.routine.disabled_badge')}</span>
                        )}
                        {routine.stopAfter && (
                            <span className="text-xs text-purple-700">{t('projects.routine.stop_after_badge')}</span>
                        )}
                    </div>
                    <Icon type={active === routine._id ? 'up' : 'down'} className="mr-2" />
                </div>
                {active === routine._id && (
                    <div>
                        <RoutineForm
                            _id={routine._id}
                            authorId={authorId}
                            projectId={projectId}
                            changeId={changeId}
                            onCancel={ closeRoutine }
                            onRemove={() => setToDelete(routine._id)}
                            onDuplicate={ duplicateRoutine }
                        />
                    </div>
                )}
            </div>
        );
    }, [active, t]);

    return (
        <div className="routines flex flex-row">
            <div className="flex-1 flex flex-col space-y-5">
                {sortedRoutines.map((routine: Routine) => routineForm(routine)) }
                <div className="flex flex-row items-center space-x-0 border bg-blue-100 dark:bg-blue-900/40 rounded mx-2">
                    <label className="max-w-[200px] flex-1 flex items-center text-sm px-2">
                        {t('projects.add_routine')}
                    </label>
                    <div className="flex-1">
                        <input
                            type="text"
                            value={newRoutineName}
                            onChange={(e: any) => { setNewRoutineName(e.target.value); }}
                            onKeyUp={e => handleKeyPress(e, addRoutine)}
                            placeholder={t('projects.add_routine_placeholder') || ''}
                            className="border-0 rounded rounded-l-none text-sm focus:ring-0 w-full"
                        />
                    </div>
                    {newRoutineName && (
                        <Button
                            className="h-full rounded-l-none"
                            small
                            color="primary"
                            icon="plus"
                            iconSize={4}
                            title={t('common.add')}
                            onClick={ addRoutine }
                        />
                    )}
                </div>
                {projectId && (
                    <div className="mx-2 flex flex-col gap-2">
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                            {t('projects.routine.seed_examples_desc')}
                        </p>
                        <div>
                            <Button
                                type="button"
                                small
                                color="primary"
                                icon={seeding ? 'loading' : 'plus'}
                                title={t('projects.routine.seed_examples')}
                                onClick={seedExamples}
                                disabled={seeding}
                            />
                        </div>
                        {seedFeedback && (
                            <Alert
                                title={seedFeedback.message}
                                icon={seedFeedback.tone === 'failure' ? 'warning' : 'check'}
                                color={seedFeedback.tone === 'success' ? 'success' : seedFeedback.tone === 'warning' ? 'warning' : 'failure'}
                            />
                        )}
                    </div>
                )}
                {runs.length > 0 && (
                    <div className="mx-2 border rounded p-2 bg-gray-50 dark:bg-gray-900">
                        <div className="text-sm font-bold mb-2">{t('projects.routine.execution_log')}</div>
                        <ul className="text-xs space-y-1">
                            {runs.map((run) => (
                                <li key={run._id} className="flex justify-between gap-2">
                                    <span>
                                        {run.event} → {String(run.routine).slice(0, 8)}…
                                        {run.stoppedChain ? ` [${t('projects.routine.stop_after_badge')}]` : ''}
                                    </span>
                                    <span className={run.success ? 'text-green-700' : 'text-red-600'}>
                                        {run.success ? 'OK' : 'ERR'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <Confirm
                visible={!!toDelete}
                title={ t('projects.are_you_sure_to_delete_this_routine') || '' }
                onConfirm={() => { removeRoutine() }}
                onCancel={() => setToDelete(null)}
            />
        </div>
    );
}

export default RoutinesManager;
